import { DatePipe } from '@angular/common';
import { Component, effect, inject, input, OnDestroy, output, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule, MatDateRangePicker } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { TNil } from '@app/app.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ICampaignAnalyticsFilter } from '@pages/campaigns/campaign-details/campaign-details.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { DatePickerUtilitiesService } from '@utilities/date-picker-utilities.service';
import { endOfDay, isBefore, startOfDay } from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';

@Component({
  imports: [
    DatePipe,
    IconComponent,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    ReactiveFormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    MatErrorComponent,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-start-end-date-picker',
  templateUrl: './start-end-date-picker.component.html',
})
export class StartEndDatePickerComponent implements OnDestroy {
  public readonly startDateControl = input.required<FormControl<Date | null>>();
  public readonly endDateControl = input.required<FormControl<Date | null>>();

  public readonly initialEndDate = input<Date | null>(null);
  public readonly initialStartDate = input<Date | null>(null);
  public readonly startDateLabel = input<string>();
  public readonly endDateLabel = input<string>();
  public readonly loading = input<boolean>(false);

  public readonly dateRangeChanged = output<Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>>();

  public readonly startDate = signal<Date | null>(null);
  public readonly endDate = signal<Date | null>(null);

  public readonly anyPickersOpen = signal<boolean>(false);

  public form = new FormGroup({
    endDate: new FormControl<Date | null>(null, [Validators.required]),
    startDate: new FormControl<Date | null>(null, [Validators.required]),
  });

  private readonly startRangePicker = viewChild.required<MatDateRangePicker<Date>>('startRangePicker');
  private readonly endDatePicker = viewChild.required<MatDatepicker<Date>>('endDatePicker');

  public readonly datePickerUtilities = inject(DatePickerUtilitiesService);

  private isSyncingPicker = false;
  private lastEmittedDateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'> | null = null;

  private startPickerSubscription: Subscription | null = null;
  private endPickerSubscription: Subscription | null = null;
  private startDateSubscription: Subscription | TNil = null;
  private endDateSubscription: Subscription | TNil = null;

  private initialEndDateSet = false;
  private initialStartDateSet = false;

  private initialEndDateConsumed = false;
  private initialStartDateConsumed = false;

  private clonedInitialEndDate = signal<Date | null>(null);
  private clonedInitialStartDate = signal<Date | null>(null);

  constructor() {
    this.setupFormSync();

    effect(() => {
      if (!this.endDateControl() || !this.startDateControl()) {
        return;
      }

      this.setupPickerSubscriptions();
      this.setupFormValueChanges();
    });

    effect(() => {
      const initialEndDate = this.initialEndDate();

      if (!this.initialEndDateConsumed) {
        this.clonedInitialEndDate.set(initialEndDate ? new Date(initialEndDate) : null);
        this.initialEndDateConsumed = true;
      }
    });

    effect(() => {
      const initialStartDate = this.initialStartDate();

      if (!this.initialStartDateConsumed) {
        this.clonedInitialStartDate.set(initialStartDate ? new Date(initialStartDate) : null);
        this.initialStartDateConsumed = true;
      }
    });
  }

  public openStartPicker(event: Event): void {
    event.stopImmediatePropagation();

    if (this.endDatePicker()) {
      this.endDatePicker().close();
    }

    this.anyPickersOpen.set(true);

    requestAnimationFrame(() => {
      this.syncPickerSelections();
      this.startRangePicker().open();
    });
  }

  public openEndPicker(event: Event): void {
    event.stopImmediatePropagation();

    if (this.startRangePicker()) {
      this.startRangePicker().close();
    }

    this.anyPickersOpen.set(true);

    requestAnimationFrame(() => {
      this.syncPickerSelections();
      this.endDatePicker().open();
    });
  }

  public ngOnDestroy(): void {
    this.startPickerSubscription?.unsubscribe();
    this.endPickerSubscription?.unsubscribe();
    this.startDateSubscription?.unsubscribe();
    this.endDateSubscription?.unsubscribe();
  }

  private setupFormSync(): void {
    effect(() => {
      if (!this.endDateControl() || !this.startDateControl()) {
        return;
      }

      const startDate = this.startDate();
      const endDate = this.endDate();

      this.form = new FormGroup({
        endDate: this.endDateControl(),
        startDate: this.startDateControl(),
      });

      if (startDate !== this.form.get('startDate')?.value) {
        this.form.patchValue({ startDate }, { emitEvent: false });
      }

      if (endDate !== this.form.get('endDate')?.value) {
        this.form.patchValue({ endDate }, { emitEvent: false });
      }

      if (this.clonedInitialEndDate() && !this.initialEndDateSet) {
        this.form.patchValue({ endDate: this.clonedInitialEndDate() }, { emitEvent: false });
        this.initialEndDateSet = true;
        this.ensureEndDateWhenOnlyStartSelected();
      }

      if (this.clonedInitialStartDate() && !this.initialStartDateSet) {
        this.form.patchValue({ startDate: this.clonedInitialStartDate() }, { emitEvent: false });
        this.initialStartDateSet = true;
        this.ensureEndDateWhenOnlyStartSelected();
      }
    });
  }

  private setupPickerSubscriptions(): void {
    this.startPickerSubscription?.unsubscribe();
    this.endPickerSubscription?.unsubscribe();

    if (this.startRangePicker()) {
      this.startPickerSubscription = this.startRangePicker().closedStream.subscribe(() => {
        this.ensureEndDateWhenOnlyStartSelected();
        this.removeFloatingLabel();

        requestAnimationFrame(() => {
          this.syncPickerSelections();
        });
      });
    }

    if (this.endDatePicker()) {
      this.endPickerSubscription = this.endDatePicker().closedStream.subscribe(() => {
        this.ensureEndDateWhenOnlyStartSelected();
        this.removeFloatingLabel();

        requestAnimationFrame(() => {
          this.syncPickerSelections();
        });
      });
    }
  }

  private setupFormValueChanges(): void {
    this.startDateSubscription?.unsubscribe();
    this.endDateSubscription?.unsubscribe();

    this.startDateSubscription = this.form.get('startDate')?.valueChanges.subscribe((value) => {
      this.startDate.set(value);
    });

    this.endDateSubscription = this.form.get('endDate')?.valueChanges.subscribe((value) => {
      const normalizedEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
        value as Date | null,
        (this.form.get('startDate')?.value as Date | null) ?? null,
      );

      if (value && normalizedEndDate && value.getTime() !== normalizedEndDate.getTime()) {
        this.form.get('endDate')?.setValue(normalizedEndDate, { emitEvent: false });
      }

      this.endDate.set(normalizedEndDate ? endOfDay(normalizedEndDate) : null);
    });
  }

  private syncPickerSelections(): void {
    if (this.isSyncingPicker) {
      return;
    }

    const startPicker = this.startRangePicker();
    const endPicker = this.endDatePicker();

    if (startPicker?.opened || endPicker?.opened) {
      return;
    }

    this.isSyncingPicker = true;

    const startDate = this.startDate() || this.clonedInitialStartDate();
    const endDate = this.endDate() || this.clonedInitialEndDate();

    setTimeout(() => {
      if (startPicker && !startPicker.opened) {
        if (startDate) {
          startPicker.select(startDate);
        }

        if (endDate) {
          startPicker.select(endDate);
        }
      }

      if (endPicker && !endPicker.opened && endDate) {
        endPicker.select(endDate);
      }

      this.form.controls.startDate.setValue(startDate);
      this.form.controls.endDate.setValue(endDate);

      this.emitDateRange();

      this.isSyncingPicker = false;
    });
  }

  private emitDateRange(): void {
    const endDate = this.endDate();
    const startDate = this.startDate();

    if (endDate === this.lastEmittedDateRange?.endDate && startDate === this.lastEmittedDateRange?.startDate) {
      return;
    }

    this.lastEmittedDateRange = { endDate, startDate };

    this.dateRangeChanged.emit({
      endDate: endOfDay(endDate ?? new Date()),
      startDate: startOfDay(startDate ?? new Date()),
    });
  }

  private ensureEndDateWhenOnlyStartSelected(): void {
    const startDate = (this.form.get('startDate')?.value as Date | null) || null;
    const endDate = (this.form.get('endDate')?.value as Date | null) || null;

    if (startDate && !endDate) {
      const endOfStartDate = endOfDay(startDate);
      this.form.get('endDate')?.setValue(endOfStartDate);
      this.endDate.set(endOfStartDate);

      this.syncPickerSelections();
    }

    if (startDate && endDate && isBefore(startOfDay(endDate), startOfDay(startDate))) {
      const endOfStartDate = endOfDay(startDate);
      this.form.get('endDate')?.setValue(endOfStartDate);
      this.endDate.set(endOfStartDate);

      this.syncPickerSelections();
    }
  }

  public onEndDateChange(date: Date | null): void {
    const validEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
      date,
      (this.form.get('startDate')?.value as Date | null) ?? null,
    );

    if (!validEndDate) {
      return;
    }

    this.form.get('endDate')?.setValue(validEndDate);
  }

  private removeFloatingLabel(): void {
    if (this.startDateControl().value || this.endDateControl().value) {
      return;
    }

    setTimeout(() => {
      const formFields = document.querySelectorAll('app-start-end-date-picker label');

      formFields.forEach((formField) => {
        if (formField instanceof HTMLElement) {
          formField.classList.remove('mdc-floating-label--float-above');
        }
      });

      this.anyPickersOpen.set(false);
    }, 130);
  }
}
